// Shared dictation helper (Theme 8 contact-enrichment round, 2026-08-10) — the receipts/minutes
// MediaRecorder → /api/dictate pattern, extracted so a module attaches a mic in one call instead
// of copy-pasting ~55 lines (receipts/minutes/connect keep their own copies for now — adopting
// this helper there is a later cleanup). The button styles via its `data-dict` attribute
// (idle | rec | busy), same as the existing per-module mics.
import { toast } from "./app";

function pickMime(): { mime: string; f: string } {
  const cands = [
    { mime: "audio/mp4", f: "m4a" }, // iOS Safari
    { mime: "audio/webm;codecs=opus", f: "webm" },
    { mime: "audio/webm", f: "webm" },
  ];
  for (const c of cands) if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(c.mime)) return c;
  return { mime: "", f: "webm" };
}

/** Wire a mic button: tap = record, tap again = stop → transcribe → `onText(text)`. */
export function attachDictation(btn: HTMLElement, onText: (text: string) => void): void {
  let media: MediaRecorder | null = null;
  let stream: MediaStream | null = null;
  let chunks: Blob[] = [];
  let fmt = "webm";
  const set = (s: "idle" | "rec" | "busy") => btn.setAttribute("data-dict", s);
  set("idle");

  async function finish(): Promise<void> {
    const blob = new Blob(chunks);
    chunks = [];
    stream?.getTracks().forEach((t) => t.stop()); stream = null; media = null;
    if (blob.size < 1200) { set("idle"); return; }
    set("busy");
    try {
      const b64 = await new Promise<string>((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => { const s = String(fr.result || ""); res(s.slice(s.indexOf(",") + 1)); };
        fr.onerror = () => rej(new Error("read_failed"));
        fr.readAsDataURL(blob);
      });
      const r = await fetch("/api/dictate", {
        method: "POST", headers: { "content-type": "application/json" }, credentials: "include",
        body: JSON.stringify({ audio_b64: b64, audio_format: fmt }),
      });
      const j = (await r.json().catch(() => ({}))) as { text?: string; error?: string };
      if (j.text) onText(j.text);
      else if (j.error === "no_speech") toast("We couldn't hear any speech — try again");
      else toast("Dictation failed — try again");
    } catch { toast("Dictation failed — check your connection"); }
    finally { set("idle"); }
  }

  btn.addEventListener("click", () => {
    void (async () => {
      const s = btn.getAttribute("data-dict") || "idle";
      if (s === "busy") return;
      if (s === "rec") { try { media?.stop(); } catch { /* onstop runs */ } return; }
      try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); }
      catch { toast("We couldn't access your microphone"); return; }
      const { mime, f } = pickMime();
      fmt = f; chunks = [];
      try { media = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream); }
      catch { media = new MediaRecorder(stream!); }
      media.ondataavailable = (ev) => { if (ev.data?.size) chunks.push(ev.data); };
      media.onstop = () => void finish();
      media.start();
      set("rec");
    })();
  });
}
